import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Send, Paperclip, Download, Clock, Tag, User, FileText, X, ArrowLeft, CheckCircle, Loader } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { UPLOAD_URL } from '../../config';

const statusColors = {
  'Yeni': 'bg-blue-100 text-blue-700 border-blue-200',
  'İşleme Alındı': 'bg-purple-100 text-purple-700 border-purple-200',
  'Devam Ediyor': 'bg-amber-100 text-amber-700 border-amber-200',
  'Çözüldü': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Logo\'ya İletildi': 'bg-indigo-100 text-indigo-700 border-indigo-200',
};

const statusSteps = ['Yeni', 'İşleme Alındı', 'Devam Ediyor', 'Çözüldü'];

export default function PublicTicketView() {
  const { token } = useParams();
  const [ticket, setTicket] = useState(null);
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => { loadTicket(); }, [token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages]);

  const loadTicket = async () => {
    try {
      const res = await api.get(`/public/track/${token}`);
      setTicket(res.data);
      setError(null);
    } catch (err) {
      setError('Talep bulunamadı veya geçersiz bağlantı.');
    }
    setLoading(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() && files.length === 0) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('message', message);
      files.forEach(f => formData.append('files', f));
      await api.post(`/public/track/${token}/message`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMessage('');
      setFiles([]);
      loadTicket();
      toast.success('Mesajınız gönderildi');
    } catch (err) {
      toast.error('Mesaj gönderilemedi');
    }
    setSending(false);
  };


  const getStepIndex = () => {
    if (!ticket) return 0;
    const idx = statusSteps.indexOf(ticket.Status);
    return idx >= 0 ? idx : 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <img src="/logo.png" alt="DSY" className="h-14 mx-auto mb-4" />
          <Loader size={32} className="text-blue-600 animate-spin mx-auto" />
          <p className="text-slate-500 mt-3">Talep yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-lg p-10 max-w-md">
          <img src="/logo.png" alt="DSY" className="h-14 mx-auto mb-6" />
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Talep Bulunamadı</h2>
          <p className="text-slate-500 mb-6">{error}</p>
          <Link to="/request" className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700">
            Yeni Talep Oluştur
          </Link>
        </div>
      </div>
    );
  }

  const stepIndex = getStepIndex();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="DSY" className="h-10" />
            <div>
              <h1 className="text-lg font-bold text-slate-800">Talep Takibi</h1>
              <p className="text-xs text-slate-500">#{ticket.Id} numaralı talebiniz</p>
            </div>
          </div>
          <Link to="/request" className="text-sm text-blue-600 hover:underline font-medium">Yeni Talep</Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Status Progress Bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800">{ticket.Title}</h2>
            <span className={`text-xs px-3 py-1.5 rounded-full font-semibold border ${statusColors[ticket.Status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
              {ticket.Status}
            </span>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-0 mt-6">
            {statusSteps.map((s, i) => (
              <div key={s} className="flex-1 flex items-center">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    i <= stepIndex ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-slate-200 text-slate-500'
                  }`}>
                    {i < stepIndex ? <CheckCircle size={20} /> : i + 1}
                  </div>
                  <p className={`text-xs mt-2 font-medium text-center ${i <= stepIndex ? 'text-blue-700' : 'text-slate-400'}`}>{s}</p>
                </div>
                {i < statusSteps.length - 1 && (
                  <div className={`h-1 flex-1 rounded -mt-6 ${i < stepIndex ? 'bg-blue-600' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Messages */}
          <div className="lg:col-span-2">
            {ticket.Description && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Açıklama</h3>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{ticket.Description}</p>
                {ticket.files?.filter(f => !f.MessageId).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                    {ticket.files.filter(f => !f.MessageId).map(file => (
                      <a key={file.Id} href={`${file.FilePath.replace('/uploads', UPLOAD_URL)}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg hover:bg-blue-50 transition-colors group">
                        <Download size={14} className="text-slate-400 group-hover:text-blue-600" />
                        <span className="text-xs text-slate-600 truncate group-hover:text-blue-600">{file.OriginalName}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-700">Mesajlar & Güncellemeler</h3>
              </div>
              <div className="p-5 space-y-4 max-h-[500px] overflow-y-auto">
                {ticket.messages?.length === 0 ? (
                  <p className="text-center text-slate-400 py-8 text-sm">Henüz mesaj yok. Personelimiz en kısa sürede yanıt verecektir.</p>
                ) : ticket.messages?.map(msg => (
                  <div key={msg.Id} className={`flex ${msg.UserRole !== 'customer' && msg.UserRole ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.UserRole !== 'customer' && msg.UserRole
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-800'
                    }`}>
                      <p className={`text-xs font-semibold mb-1 ${msg.UserRole !== 'customer' && msg.UserRole ? 'text-blue-100' : 'text-slate-500'}`}>
                        {msg.UserRole !== 'customer' && msg.UserRole ? `${msg.UserName} (Destek)` : (ticket.ContactName || msg.UserName || 'Müşteri')}
                      </p>
                      <p className="text-sm whitespace-pre-wrap">{msg.Message}</p>
                      {ticket.files?.filter(f => f.MessageId === msg.Id).length > 0 && (
                        <div className="mt-2 space-y-1">
                          {ticket.files.filter(f => f.MessageId === msg.Id).map(file => (
                            <a key={file.Id} href={`${file.FilePath.replace('/uploads', UPLOAD_URL)}`} target="_blank" rel="noopener noreferrer"
                              className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md ${msg.UserRole !== 'customer' && msg.UserRole ? 'bg-white/20 text-blue-100 hover:bg-white/30' : 'bg-white/60 text-blue-600 hover:bg-white'}`}>
                              <Download size={12} />{file.OriginalName}
                            </a>
                          ))}
                        </div>
                      )}
                      <p className={`text-xs mt-1 ${msg.UserRole !== 'customer' && msg.UserRole ? 'text-blue-200' : 'text-slate-400'}`}>
                        {new Date(msg.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Send Message */}
              <form onSubmit={handleSend} className="border-t border-slate-100 p-4">
                <p className="text-[11px] text-blue-500 mb-2 flex items-center gap-1">
                  <span>📋</span> Ekran görüntüsünü <kbd className="px-1 py-0.5 bg-blue-100 rounded font-mono text-[10px] font-bold">Ctrl+V</kbd> ile yapıştırabilirsiniz
                </p>
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {files.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-lg">
                        <FileText size={12} />{f.name}
                        <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2}
                    onPaste={e => {
                      const items = e.clipboardData?.items;
                      if (!items) return;
                      for (const item of items) {
                        if (item.type.startsWith('image/')) {
                          e.preventDefault();
                          const file = item.getAsFile();
                          if (file) {
                            const named = new File([file], `screenshot-${Date.now()}.png`, { type: file.type });
                            setFiles(prev => [...prev, named].slice(0, 5));
                          }
                        }
                      }
                    }}
                    className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                    placeholder="Yanıtınızı yazın..." />
                  <label className="p-2.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl cursor-pointer transition-colors">
                    <Paperclip size={20} />
                    <input type="file" multiple onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files)].slice(0, 5))} className="hidden" />
                  </label>
                  <button type="submit" disabled={sending}
                    className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50">
                    {sending ? <Loader size={20} className="animate-spin" /> : <Send size={20} />}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar Info */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 space-y-4">
              <h3 className="text-sm font-semibold text-slate-700">Talep Bilgileri</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-slate-400">Talep No</p>
                  <p className="text-lg font-bold text-blue-600">#{ticket.Id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Kategori</p>
                  <p className="text-sm font-medium text-slate-700">{ticket.Category}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Öncelik</p>
                  <p className="text-sm font-medium text-slate-700">{ticket.Priority}</p>
                </div>
                {ticket.AssignedToName && (
                  <div>
                    <p className="text-xs text-slate-400">İlgilenen Personel</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-7 h-7 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {ticket.AssignedToName.charAt(0)}
                      </div>
                      <p className="text-sm font-medium text-slate-700">{ticket.AssignedToName}</p>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-400">Oluşturulma</p>
                  <p className="text-sm text-slate-700">{new Date(ticket.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}</p>
                </div>
              </div>
            </div>

            {ticket.files?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Dosyalar</h3>
                <div className="space-y-2">
                  {ticket.files.map(file => (
                    <a key={file.Id} href={`${file.FilePath.replace('/uploads', UPLOAD_URL)}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg hover:bg-blue-50 transition-colors group">
                      <Download size={16} className="text-slate-400 group-hover:text-blue-600" />
                      <span className="text-sm text-slate-700 truncate group-hover:text-blue-600">{file.OriginalName}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}


            <Link to="/request"
              className="block text-center px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors text-sm">
              Yeni Talep Oluştur
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
